import { useCallback, useSyncExternalStore } from 'react'

export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'nogrok-theme'

function readThemeFromDom(): Theme {
  if (typeof document === 'undefined') return 'dark'
  return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark'
}

function subscribe(callback: () => void) {
  const onCustom = () => callback()
  const onStorage = (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY || e.newValue == null) return
    if (e.newValue === 'light' || e.newValue === 'dark') {
      document.documentElement.setAttribute('data-theme', e.newValue)
      callback()
    }
  }
  window.addEventListener('nogrok-theme-change', onCustom)
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener('nogrok-theme-change', onCustom)
    window.removeEventListener('storage', onStorage)
  }
}

export function setTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme)
  try {
    localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    /* ignore quota / private mode */
  }
  window.dispatchEvent(new CustomEvent('nogrok-theme-change', { detail: theme }))
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, readThemeFromDom, () => 'dark' as Theme)

  const toggle = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark')
  }, [theme])

  return { theme, setTheme, toggle }
}
